<template>
  <div class="signup-field">
    <label
      class="field-label"
      :id="inputId + '-label'"
      >{{ inputLabel }}</label
    >
    <div class="ac-wrap">
      <input
        :id="inputId"
        ref="input"
        v-model="localLabel"
        type="text"
        class="signup-input"
        :aria-labelledby="inputId + '-label'"
        :placeholder="placeholder"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <ul
        v-show="showList"
        ref="list"
        class="signup-ac-list"
        role="listbox"
      >
        <li
          v-for="loc in displaySuggestions"
          :key="loc.id"
          class="signup-ac-item"
          role="option"
          @mousedown.prevent="select(loc)"
        >
          <span
            v-if="loc.flag"
            class="signup-ac-flag"
          >
            <component :is="loc.flag" />
          </span>
          <span class="signup-ac-nm">{{ loc.city }}</span>
          <span class="signup-ac-rg">{{ loc.city }} · {{ loc.zip_code }}</span>
        </li>
        <li
          v-if="!suggestions.length && searched"
          class="signup-ac-item signup-ac-empty"
          aria-disabled="true"
        >
          <span class="signup-ac-pin">·</span>
          <span class="signup-ac-nm">{{ noResultsText }}</span>
        </li>
      </ul>
    </div>
    <div class="field-note">{{ value ? selectedHelper : helper }}</div>
  </div>
</template>

<script>
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import FlagDe from '../../icons/FlagDe.vue';
import FlagAt from '../../icons/FlagAt.vue';
import FlagCh from '../../icons/FlagCh.vue';

const DEBOUNCE_MS = 120;
// Grace period after blur before we hide the dropdown — gives a click on a
// suggestion (handled via mousedown) time to register first. Independent of
// the search-debounce delay; happens to share the same value today.
const BLUR_DELAY_MS = 120;
const MAX_RESULTS = 8;
// Vertical gap (px) between the input's bottom edge and the dropdown.
const DROPDOWN_GAP_PX = 6;
// Aesthetic cap on dropdown height (mirrors the scss fallback).
const DROPDOWN_MAX_PX = 280;
// Breathing room kept between the dropdown and the viewport edge.
const VIEWPORT_MARGIN_PX = 8;

// Country-code → flag component. Mirrors the convention in DiscoverSection.vue.
// Anything outside this map renders without a flag (no broken icon).
const FLAGS = {
  de: FlagDe,
  at: FlagAt,
  ch: FlagCh,
};

export default {
  name: 'LocationAutocomplete',
  // .sync on `value` (location id) + .sync on `label` (display string).
  // The parent owns both; we just expose two-way bindings so the form can
  // submit `location_id` while the input shows the human-readable label.
  props: {
    value: { type: [Number, String], default: null },
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    helper: { type: String, default: '' },
    // Shown in place of `helper` once a value is picked. Empty by default, so
    // the field simply clears its hint after a selection (prior behaviour).
    selectedHelper: { type: String, default: '' },
    inputLabel: { type: String, default: 'Location' },
    inputId: { type: String, default: 'signup-loc' },
    noResultsText: { type: String, default: 'No matches.' },
  },
  data() {
    return {
      localLabel: this.label,
      suggestions: [],
      // True once the user has typed enough for a filter to actually run.
      // Drives the "Keine Treffer" empty-state row so it only appears after
      // a real search (not on focus of an empty input).
      searched: false,
      // Set to the label of the selection so we can detect when the user
      // edits the input away from a previously-picked entry and clear the id.
      selectedLabel: this.label,
      searchTimer: null,
      // Floating UI autoUpdate teardown — registered only while the list is open.
      stopAutoUpdate: null,
      listAttached: false,
    };
  },
  computed: {
    storeLocations() {
      return (this.$store && this.$store.getters['locations/getLocations']) || [];
    },
    // Precomputed lowercase "city zip" search key per location. Built once when
    // the dataset loads (computed cache), so keystrokes only do substring
    // checks instead of re-lowercasing the whole list every time.
    searchIndex() {
      return this.storeLocations.map((loc) => ({
        loc,
        key: `${loc.city} ${loc.zip_code}`.toLowerCase(),
      }));
    },
    // Show the dropdown whenever we have hits OR an empty-state row to render.
    showList() {
      return this.suggestions.length > 0 || this.searched;
    },
    // Attach the flag component (if any) to each suggestion so the template
    // can render via <component :is>. `country_code` is the ISO-2 code
    // ('de'/'at'/'ch'); `country` from the API is the localised full name.
    displaySuggestions() {
      return this.suggestions.map((loc) => ({
        ...loc,
        flag: FLAGS[String(loc.country_code || '').toLowerCase()] || null,
      }));
    },
  },
  watch: {
    label(v) {
      if (v !== this.localLabel) {
        this.localLabel = v;
      }
    },
    // Dropdown opens / closes — start or stop position tracking accordingly.
    showList(open) {
      if (open) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    },
  },
  created() {
    // Best-effort preload — same call is fired again on focus if the list
    // is still empty (covers the case where the modal opens before the
    // initial store load resolved).
    if (this.$store && this.$store.dispatch) {
      this.$store.dispatch('locations/load').catch(() => {
        /* non-fatal */
      });
    }
  },
  mounted() {
    // Teleport the dropdown to <body> so it escapes the modal's
    // `overflow: hidden` and the `transform` on `.signup-modal` (which would
    // otherwise turn the modal into the containing block for any descendant
    // `position: fixed`). Floating UI keeps it aligned on scroll, resize, and
    // soft-keyboard changes (iOS + Android visualViewport).
    this.attachList();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.stopTracking();
    this.detachList();
  },
  methods: {
    attachList() {
      const list = this.$refs.list;
      if (!list || this.listAttached) {
        return;
      }
      document.body.appendChild(list);
      this.listAttached = true;
    },
    detachList() {
      const list = this.$refs.list;
      if (list && list.parentNode === document.body) {
        document.body.removeChild(list);
      }
      this.listAttached = false;
    },
    startTracking() {
      if (this.stopAutoUpdate) {
        return;
      }
      const input = this.$refs.input;
      const list = this.$refs.list;
      if (!input || !list) {
        return;
      }
      this.reposition();
      this.stopAutoUpdate = autoUpdate(input, list, () => this.reposition());
    },
    stopTracking() {
      if (this.stopAutoUpdate) {
        this.stopAutoUpdate();
        this.stopAutoUpdate = null;
      }
    },
    reposition() {
      const input = this.$refs.input;
      const list = this.$refs.list;
      if (!input || !list || !this.showList) {
        return;
      }
      const r = input.getBoundingClientRect();
      // Skip when the input is no longer rendered (rect is all zero) — e.g.
      // the user navigated to a different register step.
      if (r.width === 0 && r.height === 0) {
        return;
      }
      computePosition(input, list, {
        strategy: 'fixed',
        placement: 'bottom-start',
        middleware: [
          offset(DROPDOWN_GAP_PX),
          flip({
            fallbackPlacements: ['top-start'],
            padding: VIEWPORT_MARGIN_PX,
          }),
          shift({ padding: VIEWPORT_MARGIN_PX }),
          size({
            padding: VIEWPORT_MARGIN_PX,
            apply({ availableHeight, elements }) {
              const maxH = Math.min(Math.max(0, availableHeight), DROPDOWN_MAX_PX);
              elements.floating.style.maxHeight = `${maxH}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        Object.assign(list.style, {
          position: 'fixed',
          left: `${x}px`,
          top: `${y}px`,
          width: `${Math.round(r.width)}px`,
        });
      });
    },
    onFocus() {
      // Re-attempt the load if the store is still empty — this is the fix
      // for the "dropdown sometimes doesn't appear" report: previously we
      // dispatched once and gave up silently on failure.
      if (!this.storeLocations.length && this.$store && this.$store.dispatch) {
        this.$store
          .dispatch('locations/load')
          .then(() => {
            if (this.localLabel) {
              this.runFilter();
            }
          })
          .catch(() => {
            /* non-fatal */
          });
      }
      if (this.localLabel) {
        this.runFilter();
      }
    },
    onInput() {
      // Diverged from the picked entry — drop the id so the parent's
      // canAdvance gate prevents continuing without a real selection.
      if (this.value && this.localLabel !== this.selectedLabel) {
        this.$emit('input', null);
      }
      // Reflect typing back to parent so v-model on label stays in sync.
      this.$emit('update:label', this.localLabel);
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.runFilter, DEBOUNCE_MS);
    },
    onBlur() {
      setTimeout(() => {
        this.suggestions = [];
        this.searched = false;
      }, BLUR_DELAY_MS);
    },
    runFilter() {
      const q = (this.localLabel || '').trim().toLowerCase();
      if (!q) {
        this.suggestions = [];
        this.searched = false;
        return;
      }
      // Require every whitespace-separated token to hit somewhere in the
      // precomputed "city zip" key. This lets "10115 Berlin" and "Berlin 10115"
      // match (each token tried independently), as well as a lone city or zip.
      // Stop once we have MAX_RESULTS so we never scan the whole list need-
      // lessly on a broad query.
      const tokens = q.split(/\s+/);
      const hits = [];
      for (const entry of this.searchIndex) {
        if (tokens.every((t) => entry.key.includes(t))) {
          hits.push(entry.loc);
          if (hits.length >= MAX_RESULTS) {
            break;
          }
        }
      }
      this.suggestions = hits;
      this.searched = true;
    },
    select(loc) {
      const labelStr = `${loc.zip_code} ${loc.city} (${loc.country})`;
      this.localLabel = labelStr;
      this.selectedLabel = labelStr;
      this.suggestions = [];
      this.searched = false;
      this.$emit('input', loc.id);
      this.$emit('update:label', labelStr);
    },
  },
};
</script>
